import { ErrorBoundary } from '@/components/Rollbar';
import type {
  ApprovingInvoiceReportColumn,
  ApprovingInvoiceReportsSearchQuery,
  FetchApprovingInvoiceReportsArgs,
} from '@/context/services/approving_invoice_reports';
import { ApprovingInvoiceReportsSearchRefetchProvider } from '@/context/services/approving_invoice_reports/refetchContext';
import { ApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/type';
import { ConfirmApproveModal } from '@/features/approving_invoice_reports/components/ConfirmApproveModal';
import {
  ApproveType,
  ConfirmApproveModalProvider,
  useConfirmApproveModal,
} from '@/features/approving_invoice_reports/components/ConfirmApproveModal/context/ConfirmApproveModalContext';
import { ConfirmRejectModal } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/Modal';
import {
  ConfirmRejectModalProvider,
  useConfirmRejectModal,
} from '@/features/approving_invoice_reports/components/ConfirmRejectModal/context/ConfirmRejectModalContext';
import { ExportPaymentRequests } from '@/features/approving_invoice_reports/components/ExportPaymentRequests';
import { useExportApprovingInvoiceReportsQuery } from '@/features/approving_invoice_reports/components/List/Table/hook';
import { useResizableColumns } from '@/features/approving_invoice_reports/components/List/Table/hooks/useResizableColumns';
import { useTableHeight } from '@/features/approving_invoice_reports/components/List/Table/hooks/useTableHeight';
import {
  useEmptyState,
  useTableSelection,
} from '@/features/approving_invoice_reports/components/List/hooks';
import { Drawer } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Drawer';
import { useViewPaymentDetail } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/useViewPaymentDetail';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  ModernPaginationProps,
  Table,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, Suspense, memo, useCallback, useEffect, useMemo } from 'react';
import { RestoreDrawerAfterReturn } from '../components/RestoreDrawerAfterReturn';
import { usePictureInPictureModeContext } from '../context/PictureInPictureModeProvider';
import { TriggerFindTargetReportProvider } from '../context/TriggerFindTargetReportContext';
import { getTotalTableWidth, useColumns } from './Columns';
import styles from './Table.module.scss';
import { SortOrderState, isValidSortState } from './helpers/sortValidation';
import { MultiColumnSortState } from './types';

const cx = classnames.bind(styles);

export type InternalTableProps = Omit<
  TableProps<ApprovingInvoiceReportColumn>,
  'columns' | 'pagination' | 'emptyType'
> & {
  sortOrders?: MultiColumnSortState[];
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
};

export type HandleSelectRowProps = {
  handleSelectAll?: (checked: boolean) => void;
  isAllSelected?: boolean;
  selectedRowKeys?: string[];
  handleSelectRow?: (
    record: ApprovingInvoiceReportColumn,
    checked: boolean
  ) => void;
  isAllDisabled?: boolean;
};

export type Props = HandleSelectRowProps & {
  pagination: ModernPaginationProps;
  table: InternalTableProps;
  isSearchEmpty?: boolean;
  sortOrders?: MultiColumnSortState[];
  isLoading?: boolean;
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
};

export const ApprovingInvoiceReportsTable: FC<Props> = memo(
  ({
    pagination,
    table,
    isSearchEmpty = true,
    sortOrders,
    isLoading,
    selectedRowKeys,
    handleSelectRow,
    handleSelectAll,
    isAllSelected,
    isAllDisabled,
    onMultiColumnSort,
  }) => {
    const { columnWidths, handleResize } = useResizableColumns();

    const columns = useColumns({
      selectedRowKeys: selectedRowKeys || [],
      onSelectAll: handleSelectAll || (() => {}),
      onSelectRow: handleSelectRow || (() => {}),
      isAllSelected: isAllSelected || false,
      isAllDisabled: isAllDisabled || false,
      isLoading: isLoading ?? false,
      columnWidths,
      sortOrders,
    });

    const { refContainer, listHeight } = useTableHeight({
      marginHeight: 100,
    });

    // Handle column resize event
    const handleColumnResize = useCallback(
      (columnIndex: number, width: number) => {
        handleResize(columnIndex, width, columns);
      },
      [handleResize, columns]
    );

    const { emptyType, emptyMessage } = useEmptyState(isSearchEmpty);

    const dynamicTableWidth = useMemo(() => {
      return getTotalTableWidth(columnWidths);
    }, [columnWidths]);

    const scroll = useMemo(() => {
      return {
        x: dynamicTableWidth,
        y: `${listHeight}px`,
      };
    }, [listHeight, dynamicTableWidth]);

    // Wrapper to handle the onMultiColumnSort call - receives array from library
    const handleMultiColumnSortWrapper = useCallback(
      (sortStates: SortOrderState[]) => {
        // The library passes an array of sort states
        // Convert them to our MultiColumnSortState format and pass to handler
        if (onMultiColumnSort && Array.isArray(sortStates)) {
          const convertedSorts: MultiColumnSortState[] = sortStates
            .filter(isValidSortState)
            .map((s) => ({
              columnKey: s.columnKey,
              order: s.order,
            }));
          onMultiColumnSort(convertedSorts);
        }
      },
      [onMultiColumnSort]
    );

    return (
      <div
        className={cx(styles['container'], {
          [cx(styles['reports-has-data'])]: (table?.data?.length ?? 0) > 0,
        })}
        ref={refContainer}
        data-testid='approving-invoice-reports-table'
      >
        <Table
          emptyType={emptyType}
          {...table}
          legacy={false}
          key={pagination.currentPage}
          testId='approving-invoice-reports-table'
          emptyText={emptyMessage}
          columns={columns}
          pagination={pagination}
          hideHeaderPagination
          scroll={scroll}
          loading={isLoading}
          tableLayout='fixed'
          isMultiColumnSortEnabled
          onMultiColumnSort={handleMultiColumnSortWrapper}
          initialSortState={sortOrders}
          resizable
          onResize={handleColumnResize}
        />
      </div>
    );
  }
);
ApprovingInvoiceReportsTable.displayName = 'ApprovingInvoiceReportsTable';

export const LoadingApprovingInvoiceReportsTable: FC = memo(() => {
  const isOnline = useNetworkStatus();
  const pagination = useMemo(
    () => ({
      currentPage: 1,
      maxPage: 1,
      perPage: 1,
      maxRecord: 0,
    }),
    []
  );
  const tableProps = useMemo(
    () => ({ data: [], loading: isOnline }),
    [isOnline]
  );
  return (
    <ApprovingInvoiceReportsTable
      table={tableProps}
      pagination={pagination}
      isSearchEmpty={false}
    />
  );
});
LoadingApprovingInvoiceReportsTable.displayName =
  'LoadingApprovingInvoiceReportsTable';

type ErrorApprovingInvoiceReportsTableProps = {
  error: Error | null;
};

export const ErrorApprovingInvoiceReportsTable: FC<ErrorApprovingInvoiceReportsTableProps> =
  memo(() => {
    const pagination = useMemo(
      () => ({
        currentPage: 1,
        maxPage: 1,
        perPage: 1,
        maxRecord: 0,
      }),
      []
    );
    const tableProps = useMemo(
      () => ({ data: [], loading: false, emptyType: 'error' }),
      []
    );
    return (
      <ApprovingInvoiceReportsTable
        table={tableProps}
        pagination={pagination}
        isSearchEmpty={false}
      />
    );
  });
ErrorApprovingInvoiceReportsTable.displayName =
  'ErrorApprovingInvoiceReportsTable';

export type ApprovingInvoiceReportsTableProps = Omit<
  TableProps<ApprovingInvoiceReportColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

export type ApprovingInvoiceReportsTableViewProps = {
  searchQuery: ApprovingInvoiceReportsSearchQuery;
  onSearch: (value: ApprovingInvoiceReportsSearchQuery) => void;
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
  sortOrders?: MultiColumnSortState[];
  query: FetchApprovingInvoiceReportsArgs;
  data: ApprovingInvoiceReportColumn[];
  pagination: ModernPaginationProps;
  isFetching: boolean;
  refetch: ApprovingInvoiceReportsSearchRefetch;
};

const InnerList: FC<ApprovingInvoiceReportsTableViewProps> = memo(
  ({
    searchQuery: _searchQuery,
    onSearch,
    onMultiColumnSort,
    sortOrders,
    query,
    data,
    pagination: _pagination,
    isFetching,
    refetch,
  }) => {
    const { open: openConfirmRejectModal } = useConfirmRejectModal();
    const { openPictureInPicture, isPIPMode } =
      usePictureInPictureModeContext();
    const {
      onDrawerOpen,
      onDrawerClose,
      selectedId,
      selectedIndex,
      setSelectedIndex,
    } = useViewPaymentDetail();

    const {
      selectedRowKeys,
      handleSelectRow,
      handleSelectAll,
      isAllSelected,
      selectedRowData,
      isAllDisabled,
    } = useTableSelection(data || []);

    // Clear selections when loading starts
    useEffect(() => {
      if (isFetching && selectedRowData.length > 0) {
        handleSelectAll(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching]);

    const { t } = useTranslation();
    const { onOpenConfirmApproveModal, open: openConfirmApproveModal } =
      useConfirmApproveModal();

    /** Export Approving Invoice Reports */

    const { handleExport, isLoading: isExportLoading } =
      useExportApprovingInvoiceReportsQuery(query);

    const onPageChange = useCallback(
      (page: number) => {
        onSearch({ page });
      },
      [onSearch]
    );

    const pagination = useMemo(() => {
      return {
        ..._pagination,
        onChange: onPageChange,
        currentPage: _searchQuery.page ?? 1,
      };
    }, [_pagination, onPageChange, _searchQuery.page]);

    /** Effect check close mini mode */
    useEffect(() => {
      if (!data?.length && pagination.currentPage === 1 && isPIPMode) {
        openPictureInPicture(false);
      }
      // we only need to check if the data is empty
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
    /** Effect to set Selected Index */
    useEffect(() => {
      if (selectedIndex !== undefined) return;
      if (!data.length) {
        setSelectedIndex(0);
        return;
      }
      if (selectedId) {
        const currentIndex = data.findIndex((item) => item.id === selectedId);
        currentIndex === -1
          ? setSelectedIndex(0)
          : setSelectedIndex(currentIndex);
      }
    }, [selectedId, data, setSelectedIndex, selectedIndex]);

    const table: ApprovingInvoiceReportsTableProps = useMemo(() => {
      return {
        data,
        onRow: (data, index) => {
          return {
            onClick: () => {
              onDrawerOpen(data.id);
              setSelectedIndex(index);
            },
            className: cx({
              [styles['selected-row']]:
                selectedId === data.id || selectedRowKeys.includes(data.id),
            }),
          };
        },
      };
    }, [data, onDrawerOpen, setSelectedIndex, selectedId, selectedRowKeys]);

    /** check if the data is empty */
    const isDisabledExport = useMemo(() => {
      return !data?.length;
    }, [data]);

    const handleBulkApprove = () => {
      onOpenConfirmApproveModal({
        reportTarget: selectedRowData ?? [],
        approveType: ApproveType.MULTIPLE,
      });
    };

    return (
      <ApprovingInvoiceReportsSearchRefetchProvider value={refetch}>
        <TriggerFindTargetReportProvider
          data={data}
          onSearch={onSearch}
          pagination={pagination}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          isDataLoading={isFetching ?? false}
        >
          <RestoreDrawerAfterReturn
            isFetching={isFetching ?? false}
            onDrawerOpen={onDrawerOpen}
            onDrawerClose={onDrawerClose}
          />
          <div
            className={cx(styles['table-actions-container'], {
              [styles['table-actions-container-mb']]:
                (pagination.maxPage === 1 || isDisabledExport) && !isFetching,
            })}
          >
            <div>
              {selectedRowData && selectedRowData.length > 0 && (
                <div className={cx(styles['selection-bar'])}>
                  <span className={cx(styles['selection-count'])}>
                    {t('bulk_approve_invoice_reports_selected_items_count', {
                      count: selectedRowData?.length,
                    })}
                  </span>
                  <ButtonV2
                    isSecondary
                    size='sm'
                    className={cx(styles['bulk-approve-button'])}
                    onClick={handleBulkApprove}
                  >
                    {t('approve_invoice_report_confirm_button')}
                  </ButtonV2>
                </div>
              )}
            </div>
            <ExportPaymentRequests
              isDisabled={isDisabledExport || isExportLoading || isFetching}
              onExport={handleExport}
              isLoading={isExportLoading}
            />
          </div>
          <ApprovingInvoiceReportsTable
            pagination={pagination}
            table={table}
            sortOrders={sortOrders}
            isLoading={isFetching ?? false}
            selectedRowKeys={selectedRowKeys}
            handleSelectRow={handleSelectRow}
            handleSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            isAllDisabled={isAllDisabled}
            onMultiColumnSort={onMultiColumnSort}
          />
          {selectedId && (
            <Drawer
              open={!!selectedId}
              onClose={onDrawerClose}
              selectedId={selectedId}
              selectedIndex={selectedIndex}
            />
          )}
          {openConfirmRejectModal && (
            <ConfirmRejectModal
              onDrawerOpen={onDrawerOpen}
              onDrawerClose={onDrawerClose}
            />
          )}
          {openConfirmApproveModal && (
            <ConfirmApproveModal
              onDrawerOpen={onDrawerOpen}
              onDrawerClose={onDrawerClose}
            />
          )}
        </TriggerFindTargetReportProvider>
      </ApprovingInvoiceReportsSearchRefetchProvider>
    );
  }
);
InnerList.displayName = 'InnerList';

export const ApprovingInvoiceReportsTableView: FC<ApprovingInvoiceReportsTableViewProps> =
  memo((props) => {
    return (
      <ErrorBoundary fallback={ErrorApprovingInvoiceReportsTable}>
        <Suspense fallback={<LoadingApprovingInvoiceReportsTable />}>
          <ConfirmRejectModalProvider>
            <ConfirmApproveModalProvider>
              <InnerList {...props} />
            </ConfirmApproveModalProvider>
          </ConfirmRejectModalProvider>
        </Suspense>
      </ErrorBoundary>
    );
  });
ApprovingInvoiceReportsTableView.displayName =
  'ApprovingInvoiceReportsTableView';
